import { useCallback, useEffect, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { trackEvent } from 'src/logEvent';
import { buildGeocoderRequest, isSameGeocodeRequest } from './queries';

type UseGeocodingProps = any;

function useGeocoding(value: UseGeocodingProps) {
  const [position, setPosition] = useState<google.maps.GeocoderResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [previousGeocodeRequest, setPreviousGeocodeRequest] =
    useState<google.maps.GeocoderRequest | null>(null);
  const geocoding = useMapsLibrary('geocoding');
  const geocode = useCallback(
    async (
      geocoder: google.maps.Geocoder,
      request: google.maps.GeocoderRequest,
      locationValue: any = {},
    ) => {
      try {
        const response = await geocoder.geocode({ ...request });
        trackEvent('Location_Geocoding', {
          place: response?.results?.[0]?.formatted_address,
        });
        if (response.results && response.results[0]) {
          setPosition({
            ...response.results[0],
            // @ts-ignore
            additional_details: locationValue?.additional_details || {},
            address_string: response?.results?.[0]?.formatted_address,
          });
        } else {
          setError('Geocoding results not found.');
        }
      } catch (err: any) {
        setError(`Geocoding error: ${err.message}`);
      }
    },
    [],
  );

  useEffect(() => {
    if (!geocoding) return;

    const request = buildGeocoderRequest(value);
    if (!request) return;

    if (isSameGeocodeRequest(previousGeocodeRequest, request)) return;

    const geocoder = new google.maps.Geocoder();
    setPreviousGeocodeRequest(request);
    geocode(geocoder, request, value);
  }, [value, geocoding, geocode, previousGeocodeRequest]);

  const setDraggedPosition = useCallback(
    (latLng: google.maps.LatLng | null) => {
      if (!geocoding || !latLng) return;

      const request: google.maps.GeocoderRequest = {
        location: {
          lat: latLng.lat(),
          lng: latLng.lng(),
        },
      };

      if (isSameGeocodeRequest(previousGeocodeRequest, request)) return;
      setPreviousGeocodeRequest(request);
      const geocoder = new google.maps.Geocoder();
      geocode(geocoder, request);
    },
    [geocode, geocoding, previousGeocodeRequest],
  );

  return {
    geocodedValue: position,
    error,
    setDraggedPosition,
  };
}

export default useGeocoding;
